import React, { useEffect, useState } from "react";
import { Checkbox } from "@material-ui/core";

import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import InfoToolTip from "js/react/components/InfoToolTip";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ImportFileDialog from "js/react/views/ImportFile/ImportFileDialog";
import { FileType } from "js/core/utilities/fileSourcesAndTypes";
import { ContextSource, ContextSourceType, AiGeneratedSlideGroupType } from "common/aiConstants";

import LanguagesPopup from "./LanguagesPopup";
import ContextSourceTypesPopup from "./ContextSourceTypesPopup";
import WebPageContextInput from "./WebPageContextInput";
import FileContextInput from "./FileContextInput";
import TextContextInput from "./TextContextInput";
import SlideGroupTypesPopup from "./SlideGroupTypesPopup";
import DocumentTextDialog from "../dialogs/DocumentTextDialog";

export default function AdditionalContextInput(props: {
    contextSource: ContextSource | null;
    onContextSourceChange: (contextSource: ContextSource | null) => void;
    language: string;
    onLanguageChange: (language: string) => void;
    usePresentationAsContext?: boolean;
    onUsePresentationAsContextChange?: (usePresentationAsContext: boolean) => void;
    slideGroupType?: AiGeneratedSlideGroupType;
    onSlideGroupTypeChange?: (slideGroupType: AiGeneratedSlideGroupType) => void;
    allowedSlideGroupTypes?: AiGeneratedSlideGroupType[];
}) {
    const {
        contextSource,
        onContextSourceChange,
        language,
        onLanguageChange,
        usePresentationAsContext,
        onUsePresentationAsContextChange,
        slideGroupType,
        onSlideGroupTypeChange,
        allowedSlideGroupTypes,
    } = props;

    const [contextSourceType, setContextSourceType] = useState<ContextSourceType | "none">(contextSource?.type || "none");

    useEffect(() => {
        const newContextSourceType = contextSource?.type || "none";
        if (newContextSourceType !== contextSourceType) {
            setContextSourceType(newContextSourceType);
        }
    }, [contextSource?.type]);

    const onContextSourceTypeChange = (newContextSourceType: ContextSourceType) => {
        if (newContextSourceType === ContextSourceType.TEXT) {
            ShowDialog(DocumentTextDialog, {
                onTextChange
            });
        } else if (newContextSourceType === ContextSourceType.FILE) {
            ShowDialog(ImportFileDialog, {
                description: "Text only. Supports PDFs, Word docs or text files.",
                fileTypes: [FileType.Document],
                closeOnSuccess: true,
                onSuccess: onFileChange
            });
        } else if (newContextSourceType === ContextSourceType.WEBPAGE) {
            setContextSourceType(newContextSourceType);
        }
    };

    const onFileChange = (newFile: File | null) => {
        if (newFile !== null) {
            onContextSourceChange({ type: ContextSourceType.FILE, file: newFile });
        } else {
            onContextSourceChange(null);
            setContextSourceType("none");
        }
    };

    const onUrlChange = (newUrl: string | null) => {
        if (newUrl !== null) {
            onContextSourceChange({ type: ContextSourceType.WEBPAGE, url: newUrl });
        } else {
            onContextSourceChange(null);
            setContextSourceType("none");
        }
    };

    const onTextChange = (newText: string | null) => {
        if (newText !== null) {
            onContextSourceChange({ type: ContextSourceType.TEXT, text: newText });
        } else {
            onContextSourceChange(null);
            setContextSourceType("none");
        }
    };

    return (
        <FlexLayout gap={100} align="flex-start">
            {onSlideGroupTypeChange && (
                <FlexLayout direction="column" gap={15} minWidth={225}>
                    <TextContent description>Slide Type</TextContent>
                    <SlideGroupTypesPopup
                        allowedSlideGroupTypes={allowedSlideGroupTypes}
                        slideGroupType={slideGroupType}
                        onSlideGroupTypeChange={onSlideGroupTypeChange}
                    />
                </FlexLayout>
            )}
            {onLanguageChange && (
                <FlexLayout direction="column" gap={15} minWidth={225}>
                    <TextContent description>Language</TextContent>
                    <LanguagesPopup
                        selectedLanguage={language}
                        onSelectedLanguageChange={onLanguageChange}
                    />
                </FlexLayout>
            )}

            <FlexLayout gap={15} direction="column" width={"fill"}>
                <FlexLayout gap={5}>
                    <TextContent description>Add Additional Context</TextContent>
                    <InfoToolTip title={<>
                        Improve results by adding a file, text or website to your prompt for the AI to use as reference when generating your presentation.
                    </>} />
                </FlexLayout>
                <>
                    {(contextSourceType === "none") && (
                        <ContextSourceTypesPopup onContextSourceTypeChange={onContextSourceTypeChange} />
                    )}
                    {contextSourceType === ContextSourceType.FILE && (
                        <FileContextInput file={contextSource?.file} onFileChange={onFileChange} />
                    )}
                    {contextSourceType === ContextSourceType.WEBPAGE && (
                        <WebPageContextInput url={contextSource?.url} onUrlChange={onUrlChange} />
                    )}
                    {contextSourceType === ContextSourceType.TEXT && (
                        <TextContextInput text={contextSource?.text} onTextChange={onTextChange} />
                    )}
                </>
                {onUsePresentationAsContextChange && (
                    <FlexLayout gap={5} height={20}>
                        <Checkbox
                            size="small"
                            color="primary"
                            style={{ padding: 0 }}
                            checked={usePresentationAsContext}
                            onChange={(e) => {
                                onUsePresentationAsContextChange(e.target.checked);
                            }}
                        />
                        <TextContent note bold center>Use presentation content as context</TextContent>
                    </FlexLayout>
                )}
            </FlexLayout>
        </FlexLayout>
    );
}
